import { DataModel } from "@toolpad/core/Crud";
import { useEffect, useState } from "react";

import { IVehicleFirmwareDto } from "../generated/api-client";
import { nameof } from "../utils/object.utils";
import MagicCrud, { MagicCrudFields } from "../components/MagicCrud";
import DownloadButton from "../components/DownloadButton";
import { getBaseUrl } from "../core/config";
import UploadField from "../components/UploadField";
import { axiosInstance } from "../core/axios-interceptors";
import { useToast } from "../components/ToastContext";

type VehicleFirmwareDtoDataModel = IVehicleFirmwareDto & DataModel;

export default function VehicleFirmwaresPage() {
  const { showToast } = useToast();
  const [vehicleModels, setVehicleModels] = useState<
    { value: string; label: string }[]
  >([]);

  useEffect(() => {
    const loadVehicleModels = async () => {
      try {
        const response = await axiosInstance.get(
          `${getBaseUrl()}/vehicle-models?$count=false`
        );
        if (response.data.value) {
          const options = (response.data.value as any[]).map((model) => ({
            value: model.Id,
            label: model.Name || model.Code || model.Id,
          }));
          setVehicleModels(options);
        }
      } catch (error) {
        showToast("Failed to load vehicle models", "error");
      }
    };

    loadVehicleModels();
  }, [showToast]);
  const columns: MagicCrudFields<IVehicleFirmwareDto>[] = [
    {
      field: nameof<IVehicleFirmwareDto>("id"),
      type: "number",
      extraType: "guid",
    },
    {
      field: nameof<IVehicleFirmwareDto>("description"),
      type: "string",
    },
    {
      field: nameof<IVehicleFirmwareDto>("version"),
      type: "string",
      notInEditMode: true,
      isRequired: true,
    },
    {
      field: nameof<IVehicleFirmwareDto>("vehicleModelId"),
      headerName: "model",
      type: "singleSelect",
      extraType: "guid",
      valueOptions: vehicleModels,
      notInEditMode: true,
      isRequired: true,
    },
    {
      field: nameof<IVehicleFirmwareDto>("released"),
      type: "boolean",
    },
    {
      field: nameof<IVehicleFirmwareDto>("fileUrl"),
      type: "custom",
      notInEditMode: true,
      isRequired: true,
      renderCell: (p) => {
        return (
          <DownloadButton
            fileUrl={`${getBaseUrl()}/vehicles/firmwares/${p.row["id"]}/download`}
            fileName={p.value.split("/").pop()}
          ></DownloadButton>
        );
      },
      renderFormField: ({ value, onChange, error }) => (
        <UploadField value={value} onChange={onChange} error={error} />
      ),
    },
  ];

  return (
    <MagicCrud<VehicleFirmwareDtoDataModel>
      name="vehicleFirmware"
      apiUrl="vehicles/firmwares"
      columns={columns}
      appUrl="/vehicle-firmwares"
      customCreateOne={async (data) => {
        const formData = new FormData();
        formData.append("version", data.version!);
        formData.append("vehicleModelId", data.vehicleModelId!);
        formData.append("file", data.fileUrl!);
        formData.append("description", data.description!);
        formData.append("released", data.released ? "true" : "false");
        const res = await axiosInstance.post<VehicleFirmwareDtoDataModel>(
          `${getBaseUrl()}/vehicles/firmwares`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        return res.data;
      }}
    />
  );
}
